import * as _ from 'lodash';
import {DateUtils} from "../PA.Core/Basic/Dates";
import * as AsyncLock from 'async-lock';
import {
    Product_Hierarchy_Common_Levels,
    Product_Hierarchy_Max_Depth
} from "../PA.MD.Core/MD.Constants";
import {PASearch} from "../PA.Core/PASearch";
import {ILease} from "./PA.UtilsIF";
import {LeaseRegistry} from "./LeaseRegistry";
import {GenericLease} from "./GenericLease";
import {IDateRange} from "../PA.Core/PA.Core.IF.Basic";

export class ProductHierarchyLink {
    relWbID: number;
    childWbID: number;
    parentWbID: number;
    childOrgLevel: string;
    parentOrgLevel: string;
    effectiveStartDt: Date;
    effectiveEndDt: Date;
    parentSubType?: string;

    static copyLink(anOrigLink: ProductHierarchyLink, aNewLink: ProductHierarchyLink) {
        aNewLink.relWbID = anOrigLink.relWbID;
        aNewLink.parentWbID = anOrigLink.parentWbID;
        aNewLink.parentOrgLevel = anOrigLink.parentOrgLevel;
        aNewLink.childWbID = anOrigLink.childWbID;
        aNewLink.childOrgLevel = anOrigLink.childOrgLevel;
        aNewLink.effectiveStartDt = anOrigLink.effectiveStartDt;
        aNewLink.effectiveEndDt = anOrigLink.effectiveEndDt;
        aNewLink.parentSubType = anOrigLink.parentSubType
    }
}


export class ProductHierarchyContext {

    private readonly prdHierWbID: number;
    private dctProductLinksByChildWbID: { [id: number]: ProductHierarchyLink[] } = {};
    private dctPrdGrpLinksByChildWbID: { [id: number]: ProductHierarchyLink[] } = {};
    private dctPrdGrpLinksByParentWbID: { [id: number]: ProductHierarchyLink[] } = {}; //don't need separate dictionary for FnsPrd links, since dcts are keyed by Parent WB ID, so no overlap will occur
    private dctParentByChildWbOrgLevel: { [id: string]: number } = {}; //dictionary of parent WB_ID keyed by child WB_ID + Org_Level //work on refining this dictionary later
    private dctLinksByPrdGrpWbIDOrgLevel: { [id: string]: number[] } = {}; //cache the children by parentWbID+OrgLevel
    private constructorLog: string = "";
    private dctLock: any = new AsyncLock();
    private bIsLoaded: boolean = false;
    private _lease: ILease;

    constructor(prdHWbID: number) {
        this.prdHierWbID = prdHWbID;
        this.createLease();
    }

    //return the WBIds for all products at the given Org Level
    getNodesAtOrgLevel(prdOrgLevel: string): Promise<number[]> {
        const self = this;
        return new Promise<number[]>((resolve, error) => {

            self.checkLease()
                .then((ignore) => {
                    if (!self.bIsLoaded) {
                        self.load()
                            .then((ignore) => {
                                resolve(self.getNodes(prdOrgLevel));
                                return;
                            })
                            .catch((err) => {
                                error(err);
                            });
                    } else {
                        resolve(self.getNodes(prdOrgLevel));
                        return;
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //for a given Prod ID, find the parent PrdGrp ID at the given level
    getProdParentAtOrgLevel(prdWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdGrpPrntOrgLevel?: string): Promise<number> {
        const self = this;
        return new Promise<number>((resolve, error) => {

            if (_.isUndefined(prdGrpPrntOrgLevel)) {
                prdGrpPrntOrgLevel = "";
            } else if (prdGrpPrntOrgLevel == Product_Hierarchy_Common_Levels.PrdLevel) {
                resolve(prdWbID);
                return;
            }
            if (_.isUndefined(prdGrpPrntOrgLevel)) {
                prdGrpPrntOrgLevel = "";
            }
            //for Prd relationships, key with Prd constant to avoid overlapping WB IDs between WB_Prod and WB_PrdGrp
            let dctKey: string = prdWbID.toString() + Product_Hierarchy_Common_Levels.PrdLevel + prdGrpPrntOrgLevel + DateUtils.convertToSAPBW(effectiveStartDt) + DateUtils.convertToSAPBW(effectiveEndDt);

            //if looking for higher-level parent, then check the cache first
            self.dctLock.acquire('ChildProdOrgLevelLock',
                (done: any) => {

                    self.checkLease()
                        .then((ignore) => {
                            if (dctKey in self.dctParentByChildWbOrgLevel) {
                                done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                resolve(self.dctParentByChildWbOrgLevel[dctKey]);
                                return;
                            }

                            //we have to find at least the immediate parent, which is cached, and then can use the PrdGrp parent cache from then on
                            self.getProdParent(prdWbID, effectiveStartDt, effectiveEndDt)
                                .then((result) => {

                                    if (!_.isUndefined(result)) {

                                        if (result.parentOrgLevel == prdGrpPrntOrgLevel) {
                                            self.dctParentByChildWbOrgLevel[dctKey] = result.parentWbID;
                                            done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                            resolve(result.parentWbID);
                                            return;
                                        }

                                        self.getPrdGrpParentAtOrgLevel(result.parentWbID, effectiveStartDt, effectiveEndDt, prdGrpPrntOrgLevel)
                                            .then((parentWbID) => {
                                                self.dctParentByChildWbOrgLevel[dctKey] = parentWbID;
                                                done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                                resolve(parentWbID);
                                                return;
                                            })
                                            .catch((err) => {
                                                error(err);
                                            });
                                    } else {
                                        done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                        resolve(undefined);
                                        return;
                                    }
                                })
                                .catch((err) => {
                                    error(err);
                                });
                        })
                        .catch((err) => {
                            error(err);
                        });

                });
        });
    }

    getAllProdParents(prdWbID: number): Promise<{ wbID: number, dateRange: IDateRange }[]> {
        const self = this;
        return new Promise<{ wbID: number, dateRange: IDateRange }[]>((resolve, error) => {
            self.checkLease()
                .then((ignore) => {
                    return self.getProdParents(prdWbID);
                })
                .then((links: ProductHierarchyLink[]) => {
                    let retVal: { wbID: number, dateRange: IDateRange }[] = _.map(links, (aLink) => {
                        return {
                            wbID: aLink.parentWbID,
                            dateRange: {StartDt: aLink.effectiveStartDt, EndDt: aLink.effectiveEndDt}
                        };
                    });
                    resolve(retVal);
                    return;
                })
                .catch((err) => {
                    error(err);
                })


        })
    }

    //for a given PrdGrp ID, find the parent PrdGrp ID at the given level
    getPrdGrpParentAtOrgLevel(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrgLevel?: string): Promise<number> {
        const self = this;
        return new Promise<number>((resolve, error) => {
            if (_.isUndefined(prdOrgLevel)) {
                prdOrgLevel = "";
            }


            let dctKey: string = prdGrpWbID.toString() + prdOrgLevel + DateUtils.convertToSAPBW(effectiveStartDt) + DateUtils.convertToSAPBW(effectiveEndDt);

            self.dctLock.acquire('ChildPrdGrpOrgLevelLock',
                (done: any) => {

                    self.checkLease()
                        .then((ignore) => {
                            if (dctKey in self.dctParentByChildWbOrgLevel) {
                                done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                resolve(self.dctParentByChildWbOrgLevel[dctKey]);
                                return;
                            }

                            let parentLink: ProductHierarchyLink = new ProductHierarchyLink();

                            self.climbToPrdGrpParent(prdGrpWbID, effectiveStartDt, effectiveEndDt, prdOrgLevel, parentLink)
                                .then((ignore) => {
                                    if (!_.isUndefined(parentLink.relWbID)) {
                                        self.dctParentByChildWbOrgLevel[dctKey] = parentLink.parentWbID;
                                        done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                        resolve(parentLink.parentWbID);
                                        return;
                                    } else {
                                        done(null, self.dctParentByChildWbOrgLevel[dctKey]);
                                        resolve(undefined);
                                        return;
                                    }
                                })
                                .catch((err) => {
                                    error(err);
                                });
                        })
                        .catch((err) => {
                            error(err);
                        });
                });
        });
    }

    getAllPrdGrpParents(prdGrpWbID: number): Promise<{ wbID: number, dateRange: IDateRange }[]> {
        const self = this;
        return new Promise<{ wbID: number, dateRange: IDateRange }[]>((resolve, error) => {
            self.checkLease()
                .then((ignore) => {
                    return self.getPrdGrpParents(prdGrpWbID);
                })
                .then((links: ProductHierarchyLink[]) => {
                    let retVal: { wbID: number, dateRange: IDateRange }[] = _.map(links, (aLink) => {
                        return {
                            wbID: aLink.parentWbID,
                            dateRange: {StartDt: aLink.effectiveStartDt, EndDt: aLink.effectiveEndDt}
                        };
                    });
                    resolve(retVal);
                    return;
                })
                .catch((err) => {
                    error(err);
                });

        });
    }

    getChildren(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrgLevel?: string, depthLimit?: number): Promise<number[]> {
        const self = this;
        return new Promise<number[]>((resolve, error) => {
            if (_.isUndefined(depthLimit)) {
                depthLimit = Product_Hierarchy_Max_Depth;
            } else if (depthLimit < 0) {
                error("Hierarchy search depth must be positive");
                return;
            }
            if (_.isUndefined((prdOrgLevel))) {
                prdOrgLevel = "";
            }


            let orgLevelString: string = _.isUndefined(prdOrgLevel) ? "" : prdOrgLevel.toString();
            let dctKey: string = prdGrpWbID.toString() + orgLevelString + DateUtils.convertToSAPBW(effectiveStartDt) + DateUtils.convertToSAPBW(effectiveEndDt);
            let links: ProductHierarchyLink[] = [];

            self.dctLock.acquire('childrenLock',
                (done: any) => {

                    self.checkLease()
                        .then((ignore) => {
                            if (dctKey in self.dctLinksByPrdGrpWbIDOrgLevel) {
                                done(null, self.dctLinksByPrdGrpWbIDOrgLevel[dctKey]);
                                resolve(self.dctLinksByPrdGrpWbIDOrgLevel[dctKey]);
                                return;
                            }

                            this.getChildrenAtOrgLevel(prdGrpWbID, effectiveStartDt, effectiveEndDt, prdOrgLevel, depthLimit, links)
                                .then((ignore) => {
                                    self.dctLinksByPrdGrpWbIDOrgLevel[dctKey] = _.map(links, (aLink) => {
                                        return aLink.childWbID;
                                    });
                                    done(null, self.dctLinksByPrdGrpWbIDOrgLevel[dctKey]);
                                    resolve(self.dctLinksByPrdGrpWbIDOrgLevel[dctKey]);
                                    return;
                                })
                                .catch((err) => {
                                    done(err)
                                    error(err);
                                });
                        })
                        .catch((err) => {
                            done(err)
                            error(err);
                        });
                });
        });
    }

    isPrdGrpWbIDInHierarchy(prdGrpWbID: number): Promise<boolean> {
        const self = this;
        return new Promise<boolean>((resolve, error) => {

            self.dctLock.acquire('global',
                (done: any) => {
                    self.checkLease()
                        .then((ignore) => {
                            if (!self.bIsLoaded) {
                                return self.load()
                            } else {
                                return;
                            }
                        })
                        .then((ignore) => {
                            done(null);
                            resolve(prdGrpWbID in self.dctPrdGrpLinksByParentWbID);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                });
        });
    }

    isPrdWbIdInHierarchy(prdWbID: number): Promise<boolean> {
        const self = this;
        return new Promise<boolean>((resolve, error) => {

            self.dctLock.acquire('global',
                (done: any) => {
                    self.checkLease()
                        .then((ignore) => {
                            if (!self.bIsLoaded) {
                                return self.load()
                            } else {
                                return;
                            }
                        })
                        .then((ignore) => {
                            done(null);
                            resolve(prdWbID in self.dctProductLinksByChildWbID);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                })
        });
    }

    //<editor-fold desc="Private methods">

    //find the parent of the given PrdGrpWbID at the given orgLevel
    private async climbToPrdGrpParent(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrgLevel: string, retVal: ProductHierarchyLink) {
        //made async to perform loop while looking up the tree for the parent
        let currentPrntWbID: number;
        let currentPrntOrgLevel: string;
        let currentLink: ProductHierarchyLink;
        let remainingDepth: number = Product_Hierarchy_Max_Depth;
        currentPrntWbID = prdGrpWbID;
        currentPrntOrgLevel = "Unknown";

        while (prdOrgLevel != currentPrntOrgLevel) {
            currentLink = await this.getPrdGrpParent(currentPrntWbID, effectiveStartDt, effectiveEndDt);

            if (!_.isUndefined(currentLink)) {
                if (prdOrgLevel == "" || currentLink.parentOrgLevel == prdOrgLevel) {
                    ProductHierarchyLink.copyLink(currentLink, retVal);
                    break;
                }

                currentPrntWbID = currentLink.parentWbID;
                currentPrntOrgLevel = currentLink.parentOrgLevel;

                remainingDepth -= 1;
                if (remainingDepth < 0) {
                    break;
                }
            } else {
                break;
            }
        }
    }

    private async getChildrenAtOrgLevel(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date, prdOrglevel: string, depthLimit: number, retVal: ProductHierarchyLink[]): Promise<void> {

        if (depthLimit < 0) {
            return;
        }
        let newDepth: number = depthLimit - 1;

        let currentLinkList: ProductHierarchyLink[] = await this.getPrdGrpChildren(prdGrpWbID);

        if (_.isUndefined(currentLinkList)) {
            return;
        }

        for (let aLink of currentLinkList) {
            if (!DateUtils.RangeDatesOverlap(aLink.effectiveStartDt, aLink.effectiveEndDt, effectiveStartDt, effectiveEndDt)) {
                continue;
            }

            if (aLink.childOrgLevel == prdOrglevel || prdOrglevel == "") {
                retVal.push(aLink);
            } else {
                await this.getChildrenAtOrgLevel(aLink.childWbID, effectiveStartDt, effectiveEndDt, prdOrglevel, newDepth, retVal);
            }
        }
    }

    //get the parent record for a given PrdGrp record
    private getPrdGrpParent(prdGrpWbID: number, effectiveStartDt: Date, effectiveEndDt: Date): Promise<ProductHierarchyLink> {
        const self = this;
        return new Promise<ProductHierarchyLink>((resolve, error) => {
            self.dctLock.acquire('global',
                (done: any) => {

                    if (prdGrpWbID in self.dctPrdGrpLinksByChildWbID) {
                        let record = _.first(_.filter(self.dctPrdGrpLinksByChildWbID[prdGrpWbID], (aLink: ProductHierarchyLink) => {
                            return DateUtils.RangeDatesOverlap(aLink.effectiveStartDt, aLink.effectiveEndDt, effectiveStartDt, effectiveEndDt);
                        }));
                        done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                        resolve(record);
                        return;
                    } else if (self.bIsLoaded && !(prdGrpWbID in self.dctPrdGrpLinksByChildWbID)) {
                        done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                        resolve(undefined);
                        return;
                    }

                    self.load()
                        .then((ignore) => {
                            let record = _.first(_.filter(self.dctPrdGrpLinksByChildWbID[prdGrpWbID], (aLink: ProductHierarchyLink) => {
                                return DateUtils.RangeDatesOverlap(aLink.effectiveStartDt, aLink.effectiveEndDt, effectiveStartDt, effectiveEndDt);
                            }));
                            done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                            resolve(record);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                });
        });
    }

    private getPrdGrpParents(prdGrpWbID: number): Promise<ProductHierarchyLink[]> {
        const self = this;
        return new Promise<ProductHierarchyLink[]>((resolve, error) => {
            self.dctLock.acquire('global',
                (done: any) => {

                    if (prdGrpWbID in self.dctPrdGrpLinksByChildWbID) {
                        done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                        resolve(self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                        return;
                    } else if (self.bIsLoaded && !(prdGrpWbID in self.dctPrdGrpLinksByChildWbID)) {
                        done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                        resolve([]);
                        return;
                    }

                    self.load()
                        .then((ignore) => {
                            done(null, self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                            resolve(self.dctPrdGrpLinksByChildWbID[prdGrpWbID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                });
        });
    }

    private getProdParent(prdWbID: number, effectiveStartDt: Date, effectiveEndDt: Date): Promise<ProductHierarchyLink> {
        const self = this;
        return new Promise<ProductHierarchyLink>((resolve, error) => {
            self.dctLock.acquire('global',
                (done: any) => {

                    if (prdWbID in self.dctProductLinksByChildWbID) {
                        let record = _.first(_.filter(self.dctProductLinksByChildWbID[prdWbID], (aLink: ProductHierarchyLink) => {
                            return DateUtils.RangeDatesOverlap(effectiveStartDt, effectiveEndDt, aLink.effectiveStartDt, aLink.effectiveEndDt);
                        }));
                        done(null, self.dctProductLinksByChildWbID[prdWbID]);
                        resolve(record);
                        return;
                    } else if (self.bIsLoaded && !(prdWbID in self.dctProductLinksByChildWbID)) {
                        done(null, self.dctProductLinksByChildWbID[prdWbID]);
                        resolve(undefined);
                        return;
                    }

                    self.load()
                        .then((ignore) => {
                            let record = _.first(_.filter(self.dctProductLinksByChildWbID[prdWbID], (aLink: ProductHierarchyLink) => {
                                return DateUtils.RangeDatesOverlap(aLink.effectiveStartDt, aLink.effectiveEndDt, effectiveStartDt, effectiveEndDt);
                            }));
                            done(null, self.dctProductLinksByChildWbID[prdWbID]);
                            resolve(record);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });
                });
        });
    }

    private getProdParents(prdWbID: number): Promise<ProductHierarchyLink[]> {
        const self = this;
        return new Promise<ProductHierarchyLink[]>((resolve, error) => {
            self.dctLock.acquire('global',
                (done: any) => {
                    if (prdWbID in self.dctProductLinksByChildWbID) {
                        done(null, self.dctProductLinksByChildWbID[prdWbID]);
                        resolve(self.dctProductLinksByChildWbID[prdWbID]);
                        return;
                    } else if (self.bIsLoaded && !(prdWbID in self.dctProductLinksByChildWbID)) {
                        done(null, self.dctProductLinksByChildWbID[prdWbID]);
                        resolve([]);
                        return;
                    }

                    self.load()
                        .then((ignore) => {
                            done(null, self.dctProductLinksByChildWbID[prdWbID]);
                            resolve(self.dctProductLinksByChildWbID[prdWbID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    private getPrdGrpChildren(prdGrpWbID: number): Promise<ProductHierarchyLink[]> {
        const self = this;
        return new Promise<ProductHierarchyLink[]>((resolve, error) => {
            self.dctLock.acquire('global',
                (done: any) => {
                    if (prdGrpWbID in self.dctPrdGrpLinksByParentWbID) {
                        done(null, self.dctPrdGrpLinksByParentWbID[prdGrpWbID]);
                        resolve(self.dctPrdGrpLinksByParentWbID[prdGrpWbID]);
                        return;
                    } else if (self.bIsLoaded && !(prdGrpWbID in self.dctPrdGrpLinksByParentWbID)) {
                        done(null, self.dctPrdGrpLinksByParentWbID[prdGrpWbID]);
                        resolve([]);
                        return;
                    }

                    self.load()
                        .then((ignore) => {
                            done(null, self.dctPrdGrpLinksByParentWbID[prdGrpWbID]);
                            resolve(self.dctPrdGrpLinksByParentWbID[prdGrpWbID]);
                            return;
                        })
                        .catch((err) => {
                            error(err);
                        });

                });
        });
    }

    async reset(): Promise<void> {
        this.bIsLoaded = false;
        this.dctProductLinksByChildWbID = {};
        this.dctPrdGrpLinksByChildWbID = {};
        this.dctPrdGrpLinksByParentWbID = {};
        this.dctParentByChildWbOrgLevel = {};
        this.constructorLog = "";
    }

    private createLease() {
        this._lease = new GenericLease(LeaseRegistry.Lease_Type_Product_Hierarchy);
        this._lease.addClasses(ProductHierarchyContext.getLeaseClasses(this.prdHierWbID));
    }

    static getLeaseClasses(prdHierWbID: number): string[] {
        let retVal: string[] = [];
        retVal.push(LeaseRegistry.getLeaseClass(LeaseRegistry.Lease_Subject_Product_Hierarchy, prdHierWbID));
        return retVal;
    }

    private async checkLease(): Promise<void> {
        if (!this.bIsLoaded) {
            this._lease.forceExpire();
        }

        if (await this._lease.hasDataChanged()) {
            await this.reset();
        }
    }

    private load(): Promise<void> {
        const self = this;
        return new Promise<void>((resolve, error) => {

            PASearch.find("vWB_PrdGrpR_PrdGrpProps",
                {DELETED_IND: {neq: "1"}, WB_PRDH_ID: self.prdHierWbID},
                {DELETED_IND: {type: "string"}, WB_PRDH_ID: {type: "number"}},
                ["EFFECTIVE_START_DT"], 0,
                [])
                .then((results: any[]) => {

                    _.forEach(results, (aResult) => {
                        let linkObject: ProductHierarchyLink = new ProductHierarchyLink();
                        linkObject.childWbID = aResult.WB_PRDGRP_ID;
                        linkObject.childOrgLevel = aResult.LEVEL_ID_CHILD;
                        linkObject.parentWbID = aResult.WB_PRDGRP_ID_PRNT;
                        linkObject.parentOrgLevel = aResult.LEVEL_ID_PRNT;
                        linkObject.relWbID = aResult.WB_PRDGRPR_ID;
                        linkObject.effectiveStartDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_START_DT);
                        linkObject.effectiveEndDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_END_DT);
                        linkObject.parentSubType = aResult.SUBTYPE_ID_PRNT;


                        self.cacheLinkObject(linkObject, false);
                    });

                    PASearch.find("vWB_ProdR_ProdProps",
                        {DELETED_IND: {neq: "1"}, WB_PRDH_ID: self.prdHierWbID},
                        {DELETED_IND: {type: "string"}, WB_PRDH_ID: {type: "number"}},
                        ["EFFECTIVE_START_DT"], 0,
                        [])
                        .then((results: any[]) => {

                            _.forEach(results, (aResult) => {
                                let linkObject: ProductHierarchyLink = new ProductHierarchyLink();
                                linkObject.childWbID = aResult.WB_PROD_ID;
                                linkObject.childOrgLevel = Product_Hierarchy_Common_Levels.PrdLevel;
                                linkObject.parentWbID = aResult.WB_PRDGRP_ID_PRNT;
                                linkObject.parentOrgLevel = aResult.LEVEL_ID_PRNT;
                                linkObject.relWbID = aResult.WB_PRODR_ID;
                                linkObject.effectiveStartDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_START_DT);
                                linkObject.effectiveEndDt = DateUtils.convertFromSAPBW(aResult.EFFECTIVE_END_DT);
                                linkObject.parentSubType = aResult.SUBTYPE_ID_PRNT

                                self.cacheLinkObject(linkObject, true);
                            });

                            self._lease.resetLease();
                            self.bIsLoaded = true;
                            resolve();
                        })
                        .catch((err) => {
                            error(err);
                        });
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    //Accepts a boolean indicating whether a finished-product relationship is being cached
    private cacheLinkObject(aRelLink: ProductHierarchyLink, bCacheFnsPrd: boolean) {
        //if keying by child WB ID, have to use different dictionaries for finished Prds vs PrdGrps, since WB IDs will overlap between the two tables
        let dctLinksByChildWbID: { [id: string]: ProductHierarchyLink[] } = bCacheFnsPrd ? this.dctProductLinksByChildWbID : this.dctPrdGrpLinksByChildWbID;

        if (!(aRelLink.childWbID in dctLinksByChildWbID)) {
            dctLinksByChildWbID[aRelLink.childWbID] = [];
        }
        dctLinksByChildWbID[aRelLink.childWbID].push(aRelLink);

        if (!(aRelLink.parentWbID in this.dctPrdGrpLinksByParentWbID)) {
            this.dctPrdGrpLinksByParentWbID[aRelLink.parentWbID] = [];
        }
        this.dctPrdGrpLinksByParentWbID[aRelLink.parentWbID].push(aRelLink);
    }

    private getNodes(prdOrgLevel: string): number[] {
        let retVal: number[] = [];
        if (prdOrgLevel == Product_Hierarchy_Common_Levels.PrdLevel) {
            _.forEach(_.keys(this.dctProductLinksByChildWbID), (aKey) => {
                retVal.push(parseInt(aKey));
            });
        } else {
            let dctResults: { [id: number]: number } = {}; //to store unique results with no overhead

            _.forEach(_.values(this.dctPrdGrpLinksByParentWbID), (anArray) => {
                _.forEach(anArray, (aValue) => {
                    if (aValue.parentOrgLevel == prdOrgLevel) {
                        dctResults[aValue.parentWbID] = aValue.parentWbID;
                    } else if (aValue.childOrgLevel == prdOrgLevel) {
                        dctResults[aValue.childWbID] = aValue.childWbID;
                    }
                });
            });

            retVal = _.values(dctResults);
        }

        return retVal;
    }

    //</editor-fold>

}
